import { z } from 'zod';
import { codeSchema, looseDecimal, looseInteger, } from '../common/coercion.js';
/**
 * Primitivas del contrato de Altanet WMS.
 *
 * Dos rarezas del proveedor que hay que absorber aca y no en cada schema:
 *
 * 1. **Las fechas NO son ISO 8601.** Van como `yyyy/mm/dd hh:mm:ss`, hora local
 *    de la bodega y sin zona. Una fecha ISO en un payload de Altanet es senal de
 *    que algo se mando mal: se rechaza y termina en cuarentena.
 * 2. **Los numeros conviven en dos formatos.** En `ConsultaStock`, `Stock` llega
 *    como texto con coma decimal (`"9884,000"`) mientras `Stock_pendiente_*`
 *    llegan como `number` con punto (`58.000`). No hay regla por endpoint: la
 *    coercion tiene que tolerar ambas formas en cualquier campo numerico.
 */
/** Zona horaria con la que opera la bodega. Es la del negocio, no la del servidor. */
export const ALTANET_TIME_ZONE = 'America/Santiago';
/** `yyyy/mm/dd hh:mm:ss`. Sin zona, sin milisegundos, sin `T`. */
export const ALTANET_DATE_PATTERN = /^(\d{4})\/(\d{2})\/(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;
/**
 * Descompone una fecha de Altanet. Devuelve `null` si el formato no calza o si
 * la fecha no existe en el calendario (`2026/02/30`).
 */
export function parseAltanetDateParts(value) {
    const match = ALTANET_DATE_PATTERN.exec(value.trim());
    if (!match) {
        return null;
    }
    const [, rawYear, rawMonth, rawDay, rawHour, rawMinute, rawSecond] = match;
    const parts = {
        year: Number(rawYear),
        month: Number(rawMonth),
        day: Number(rawDay),
        hour: Number(rawHour),
        minute: Number(rawMinute),
        second: Number(rawSecond),
    };
    if (parts.hour > 23 || parts.minute > 59 || parts.second > 59) {
        return null;
    }
    const asUtc = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second));
    const sameDate = asUtc.getUTCFullYear() === parts.year &&
        asUtc.getUTCMonth() === parts.month - 1 &&
        asUtc.getUTCDate() === parts.day;
    return sameDate ? parts : null;
}
/**
 * Fecha de Altanet (`yyyy/mm/dd hh:mm:ss`).
 *
 * Se valida y se devuelve tal cual, sin convertir a `Date`: el WMS no manda
 * zona horaria y convertir aca obligaria a adivinarla. La conversion es
 * responsabilidad del conector, que si conoce la zona de la bodega
 * (`ALTANET_TIME_ZONE`).
 */
export function altanetDate() {
    return z
        .string()
        .trim()
        .superRefine((value, ctx) => {
        if (parseAltanetDateParts(value) !== null) {
            return;
        }
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `La fecha "${value}" no tiene el formato de Altanet yyyy/mm/dd hh:mm:ss (Altanet NO usa ISO 8601).`,
            params: { expectedFormat: 'yyyy/mm/dd hh:mm:ss', received: value },
        });
    });
}
/**
 * Fecha de Altanet opcional. El WMS manda `""` cuando el dato no aplica
 * (fechas de lote en productos sin lote): se normaliza a `undefined`.
 */
export function altanetOptionalDate() {
    return z
        .union([z.literal(''), altanetDate()])
        .transform((value) => (value === '' ? undefined : value));
}
/**
 * Convierte una fecha de Altanet a ISO 8601 **sin zona** (`2026-08-26T12:05:18`).
 *
 * Deliberadamente no agrega `Z` ni offset: la hora es local de la bodega y
 * marcarla como UTC la correria varias horas. Quien necesite un instante
 * absoluto tiene que aplicar la zona (`ALTANET_TIME_ZONE`) explicitamente.
 */
export function altanetDateToIsoLocal(value) {
    const parts = parseAltanetDateParts(value);
    if (parts === null) {
        throw new Error(`La fecha "${value}" no tiene el formato de Altanet yyyy/mm/dd hh:mm:ss.`);
    }
    const pad = (input, size = 2) => String(input).padStart(size, '0');
    return `${pad(parts.year, 4)}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}`;
}
/**
 * Formatea un instante en el formato que espera Altanet, en la zona horaria de
 * la bodega. Se usa al construir los payloads de `CargaOROD`.
 */
export function formatAltanetDate(date, timeZone = ALTANET_TIME_ZONE) {
    if (Number.isNaN(date.getTime())) {
        throw new Error('No se puede formatear una fecha invalida para Altanet.');
    }
    const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
    const parts = {};
    for (const part of formatter.formatToParts(date)) {
        parts[part.type] = part.value;
    }
    // Algunas versiones de ICU devuelven "24" para la medianoche con hour12:false.
    const hour = parts['hour'] === '24' ? '00' : (parts['hour'] ?? '00');
    return `${parts['year'] ?? '0000'}/${parts['month'] ?? '00'}/${parts['day'] ?? '00'} ${hour}:${parts['minute'] ?? '00'}:${parts['second'] ?? '00'}`;
}
/**
 * Numero de Altanet: acepta `number` con punto y texto con coma decimal.
 * Es el mismo helper para cantidades, stock y pendientes.
 */
export function altanetDecimal(options = {}) {
    return looseDecimal(options);
}
/** Numero de linea dentro de un documento del WMS. Base 0. */
export function altanetLineNumber() {
    return looseInteger({ min: 0 });
}
/**
 * Numero de documento del WMS. Llega como texto (`"100987"`) pero se acepta
 * numero por si el proveedor cambia de opinion; la salida siempre es texto,
 * porque es lo que se guarda en `entity_xref.external_id`.
 */
export const altanetDocumentNumberSchema = z
    .union([z.string(), z.number()], {
    errorMap: () => ({ message: 'El numero de documento debe ser texto o numero.' }),
})
    .transform((value) => String(value).trim())
    .refine((value) => value !== '', 'El numero de documento no puede venir vacio.')
    .refine((value) => value.length <= 64, 'El numero de documento no puede superar 64 caracteres.');
/** Codigo de empresa, bodega o tipo de documento del WMS. */
export const altanetCodeSchema = codeSchema(16);
/** Usuario o responsable informado por el WMS. */
export const altanetUserSchema = codeSchema(64);
/** Valor con el que se reemplaza una credencial antes de persistir el payload. */
export const ALTANET_REDACTED = '[REDACTADO]';
/**
 * Devuelve una copia del payload con las credenciales del WMS tapadas.
 *
 * Altanet manda la credencial DENTRO del cuerpo (`token`, `Contrasena`), no en
 * cabeceras, asi que `redactHeaders()` no alcanza: hay que limpiar el body
 * antes de escribirlo en el message log.
 */
export function stripAltanetCredentials(payload) {
    if (payload === null || typeof payload !== 'object') {
        return payload;
    }
    if (Array.isArray(payload)) {
        return payload.map((item) => stripAltanetCredentials(item));
    }
    const source = payload;
    const result = {};
    for (const [key, value] of Object.entries(source)) {
        const isSecret = /^(token|contrasena|password|clave)$/i.test(key);
        result[key] = isSecret ? ALTANET_REDACTED : stripAltanetCredentials(value);
    }
    return result;
}
//# sourceMappingURL=primitives.js.map